import { useEffect, useRef } from "react";
import { curveMetricKeys, type GradientCurveRow } from "../api/gradient";

// Loss is bold green; metrics cycle through this palette.
const LOSS_COLOR = "#3dba6e";
const METRIC_COLORS = ["#4a9eff", "#e8a020", "#c86bff", "#46d1c0", "#ff6b9d", "#e6b450"];

function fmtVal(v: number): string {
  if (!Number.isFinite(v)) return "—";
  const a = Math.abs(v);
  if (a !== 0 && (a >= 10000 || a < 0.001)) return v.toExponential(2);
  if (a >= 100) return v.toFixed(1);
  return v.toFixed(3);
}

interface Series { name: string; color: string; key: string }

// Each series is min–max normalized to its own [0,1] track (loss and metrics
// live on wildly different scales), plotted vs step. The raw last value of each
// line is labelled inline at the right edge.
function draw(canvas: HTMLCanvasElement, rows: GradientCurveRow[]) {
  const wrap = canvas.parentElement!;
  const W = wrap.clientWidth, H = wrap.clientHeight;
  const dpr = window.devicePixelRatio || 1;
  canvas.width = W * dpr;
  canvas.height = H * dpr;
  const ctx = canvas.getContext("2d")!;
  ctx.scale(dpr, dpr);
  ctx.clearRect(0, 0, W, H);
  if (rows.length === 0) return;

  const series: Series[] = [
    { name: "loss", color: LOSS_COLOR, key: "loss" },
    ...curveMetricKeys(rows).map((k, i) => ({ name: k, color: METRIC_COLORS[i % METRIC_COLORS.length], key: k })),
  ];

  const PAD_L = 34, PAD_R = 96, PAD_T = 12, PAD_B = 22;
  const plotW = W - PAD_L - PAD_R;
  const plotH = H - PAD_T - PAD_B;
  const steps = rows.map((r) => r.step);
  const xMin = Math.min(...steps), xMax = Math.max(...steps);
  const xRange = xMax - xMin || 1;
  const sx = (x: number) => PAD_L + ((x - xMin) / xRange) * plotW;
  const sy = (t: number) => PAD_T + (1 - t) * plotH; // t in [0,1]

  // axes
  ctx.strokeStyle = "#2a2a2a";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(PAD_L, PAD_T);
  ctx.lineTo(PAD_L, PAD_T + plotH);
  ctx.lineTo(PAD_L + plotW, PAD_T + plotH);
  ctx.stroke();

  // faint horizontal gridlines (normalized track markers)
  ctx.strokeStyle = "#1e1e1e";
  for (const t of [0, 0.5, 1]) {
    ctx.beginPath();
    ctx.moveTo(PAD_L, sy(t));
    ctx.lineTo(PAD_L + plotW, sy(t));
    ctx.stroke();
  }

  // x ticks (step)
  ctx.fillStyle = "#666";
  ctx.font = "9px ui-monospace, monospace";
  ctx.textAlign = "center";
  for (let i = 0; i <= 5; i++) {
    const xv = Math.round(xMin + (xRange * i) / 5);
    ctx.fillText(`${xv}`, sx(xv), PAD_T + plotH + 12);
  }
  ctx.textAlign = "left";
  ctx.fillStyle = "#555";
  ctx.fillText("norm 0–1 / series", PAD_L + 2, PAD_T + 8);

  // one normalized line per series, with an inline last-value label
  const labels: { y: number; text: string; color: string }[] = [];
  for (const s of series) {
    const vals = rows.map((r) => r[s.key]);
    const finite = vals.filter((v) => Number.isFinite(v));
    if (finite.length === 0) continue;
    const lo = Math.min(...finite), hi = Math.max(...finite);
    const span = hi - lo || 1;
    const norm = (v: number) => (Number.isFinite(v) ? (v - lo) / span : 0.5);

    ctx.strokeStyle = s.color;
    ctx.lineWidth = s.key === "loss" ? 2 : 1.3;
    ctx.beginPath();
    let started = false;
    for (let i = 0; i < rows.length; i++) {
      const v = vals[i];
      if (!Number.isFinite(v)) { started = false; continue; }
      const x = sx(rows[i].step), y = sy(norm(v));
      if (!started) { ctx.moveTo(x, y); started = true; } else ctx.lineTo(x, y);
    }
    ctx.stroke();

    const lastRaw = finite[finite.length - 1];
    labels.push({ y: sy(norm(lastRaw)), text: `${s.name} ${fmtVal(lastRaw)}`, color: s.color });
  }

  // de-overlap the inline labels vertically, then draw at the right margin
  labels.sort((a, b) => a.y - b.y);
  const MINGAP = 12;
  for (let i = 1; i < labels.length; i++) {
    if (labels[i].y - labels[i - 1].y < MINGAP) labels[i].y = labels[i - 1].y + MINGAP;
  }
  ctx.textAlign = "left";
  ctx.font = "10px ui-monospace, monospace";
  for (const l of labels) {
    ctx.fillStyle = l.color;
    ctx.fillText(l.text, PAD_L + plotW + 6, Math.min(H - 4, Math.max(10, l.y + 3)));
  }
}

export function GradientCurves({ rows }: { rows: GradientCurveRow[] }) {
  const ref = useRef<HTMLCanvasElement | null>(null);
  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    const render = () => draw(c, rows);
    render();
    const ro = new ResizeObserver(render);
    ro.observe(c.parentElement!);
    return () => ro.disconnect();
  }, [rows]);
  return (
    <div className="viz-wrap">
      {rows.length === 0 && <div className="viz-empty">waiting for steps…</div>}
      <canvas ref={ref} />
    </div>
  );
}
